import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Me } from '../types/api';
import { userService } from '../services/userService';

interface UserState {
  isLoggedIn: boolean;
  user: Me | null;
  currentHospitalRoles: {
    roles: string[];
    permissions: string[];
  } | null;
  changePasswordLoading: boolean;
  changePasswordError: string | null;
  changePasswordSuccess: string | null;
}

const initialState: UserState = {
  isLoggedIn: false,
  user: null,
  currentHospitalRoles: null,
  changePasswordLoading: false,
  changePasswordError: null,
  changePasswordSuccess: null,
};

// Async thunk for change password
export const changePassword = createAsyncThunk(
  'user/changePassword',
  async ({ email }: { email: string }, { rejectWithValue }) => {
    try {
      const response = await userService.changePassword(email);
      return response;
    } catch (error: unknown) {
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to send change password email';
      return rejectWithValue(errorMessage);
    }
  },
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    login(state, action: PayloadAction<Me>) {
      state.isLoggedIn = true;
      state.user = action.payload;
    },
    logout(state) {
      state.isLoggedIn = false;
      state.user = null;
      state.currentHospitalRoles = null;
    },
    updateUserData(state, action: PayloadAction<Me>) {
      state.user = action.payload;
    },
    setCurrentHospitalRoles(
      state,
      action: PayloadAction<{ roles: string[]; permissions: string[] }>,
    ) {
      state.currentHospitalRoles = action.payload;
    },
    clearCurrentHospitalRoles(state) {
      state.currentHospitalRoles = null;
    },
    clearChangePasswordState(state) {
      state.changePasswordLoading = false;
      state.changePasswordError = null;
      state.changePasswordSuccess = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(changePassword.pending, (state) => {
        state.changePasswordLoading = true;
        state.changePasswordError = null;
        state.changePasswordSuccess = null;
      })
      .addCase(changePassword.fulfilled, (state, action) => {
        state.changePasswordLoading = false;
        state.changePasswordSuccess = action.payload.message;
        state.changePasswordError = null;
      })
      .addCase(changePassword.rejected, (state, action) => {
        state.changePasswordLoading = false;
        state.changePasswordError = action.payload as string;
        state.changePasswordSuccess = null;
      });
  },
});

export const {
  login,
  logout,
  updateUserData,
  setCurrentHospitalRoles,
  clearCurrentHospitalRoles,
  clearChangePasswordState,
} = userSlice.actions;
export default userSlice.reducer;
